/**
 * Her three other rooms, standing in for the ordinary tab bar for exactly as
 * long as the zone is open.
 *
 * This is a normal flow element in `shell/Shell.tsx` — the exact spot
 * `shell/TabBar.tsx` otherwise occupies — not a fixed-position overlay drawn
 * on top of it. `HerZone.tsx` itself is a `position: fixed` full-screen
 * portal, and Safari sizes a plain `fixed` element's containing block against
 * its address-bar-hidden (largest) viewport rather than whatever is actually
 * visible; anything pinned to the bottom of that box — this row, being last —
 * can end up positioned behind the address bar once it re-shows. The tab bar
 * has never had that problem because it was never `fixed` to begin with, so
 * rather than fight Safari's viewport quirks a second time, this door row
 * simply *is* the tab bar for the duration of the visit, swapped in by
 * `Shell.tsx` reading the zone's phase.
 */

import { cn } from '@gramas/ui'
import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'

interface Door {
  key: string
  label: string
  onClick: () => void
  icon: ReactNode
}

export function DoorBar({
  onByla,
  onPazadai,
  onTrofejai,
}: {
  onByla: () => void
  onPazadai: () => void
  onTrofejai: () => void
}) {
  const nav = useRef<HTMLElement>(null)

  // Same reasoning as `TabBar.tsx`'s own observer: the toast host anchors
  // itself above whichever bar is actually on screen via this property.
  useEffect(() => {
    const element = nav.current
    if (element == null) return
    const publish = () => {
      document.documentElement.style.setProperty('--tabbar-h', `${element.offsetHeight}px`)
    }
    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const doors: Door[] = [
    {
      key: 'byla',
      label: 'Byla',
      onClick: onByla,
      icon: (
        <path
          d="M3 6.5A1.5 1.5 0 014.5 5H8l1.5 1.5h6A1.5 1.5 0 0117 8v6.5a1.5 1.5 0 01-1.5 1.5h-11A1.5 1.5 0 013 14.5v-8z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      ),
    },
    {
      key: 'pazadai',
      label: 'Pažadai',
      onClick: onPazadai,
      icon: (
        <>
          <circle cx="10" cy="10" r="6.5" stroke="currentColor" strokeWidth="1.6" />
          <path
            d="M7.3 10.2l1.8 1.8 3.6-4"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      ),
    },
    {
      key: 'trofejai',
      label: 'Trofėjai',
      onClick: onTrofejai,
      icon: (
        <>
          <path
            d="M6 4h8v3.5a4 4 0 01-4 4 4 4 0 01-4-4V4z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
          <path
            d="M6 5H4.5A1.5 1.5 0 003 6.5c0 1.7 1.3 3 3 3M14 5h1.5A1.5 1.5 0 0117 6.5c0 1.7-1.3 3-3 3"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
          <path
            d="M10 11.5V14M7.5 16.5h5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </>
      ),
    },
  ]

  return (
    <nav
      ref={nav}
      aria-label="Jos kambariai"
      className={cn(
        'flex shrink-0 items-stretch border-t border-line bg-surface',
        'pb-[env(safe-area-inset-bottom)]',
      )}
    >
      {doors.map((door) => (
        <button
          key={door.key}
          type="button"
          onClick={door.onClick}
          className="group flex flex-1 flex-col items-center justify-center gap-1 py-2 text-mute transition-colors duration-[140ms] hover:text-ink"
        >
          <span className="flex size-6 items-center justify-center rounded-[var(--radius-control)] transition-transform duration-[120ms] ease-[var(--ease-out-quick)] group-active:scale-[0.96]">
            <svg viewBox="0 0 20 20" className="size-5" fill="none" aria-hidden="true">
              {door.icon}
            </svg>
          </span>
          <span className="text-[0.6875rem] leading-none">{door.label}</span>
        </button>
      ))}
    </nav>
  )
}
